import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';

interface HeaderProps {
  isLoggedIn: boolean;
  userInitials?: string;
  userName?: string;
  exportsUsed?: number;
  exportsMax?: number;
  onOpenAuth: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  isLoggedIn,
  userInitials = 'CR',
  userName = 'Cris',
  exportsUsed = 0,
  exportsMax = 5,
  onOpenAuth,
}) => {
  const { signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  return (
    <header className="fs-header">
      <div className="fs-header__inner">
        {/* Logo */}
        <a href="#" className="fs-logo">
          <img
            src="/src/assets/FORMA-Studio_logo_forma_icon_w.png"
            alt="FORMA Studio"
            className="fs-logo__icon"
          />
          <span className="fs-logo__name">FORMA Studio</span>
          <span className="fs-badge-beta-header">Beta</span>
        </a>

        {/* Desktop nav */}
        <nav className="fs-nav">
          <a href="#apps" className="fs-nav__link fs-nav__link--active">Apps</a>
          <a href="#community" className="fs-nav__link">Community</a>
          <a href="#about" className="fs-nav__link">About</a>
        </nav>

        {/* CTA visitor */}
        {!isLoggedIn && (
          <button
            className="fs-btn-primary fs-header__cta-visitor"
            style={{ fontSize: 12, padding: '8px 16px' }}
            onClick={onOpenAuth}
          >
            Join the Beta
          </button>
        )}

        {/* Account menu (logged in) */}
        {isLoggedIn && (
          <div className="fs-account-menu fs-header__cta-logged" ref={menuRef}>
            <button
              className="fs-account-menu__trigger"
              onClick={() => setMenuOpen(v => !v)}
              aria-expanded={menuOpen}
            >
              {/* Avatar */}
              <div style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'var(--fs-surface-hi)',
                border: '1.5px solid var(--fs-border-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--fs-font-headline)',
                fontWeight: 700,
                fontSize: 11,
                color: 'var(--fs-cyan)',
              }}>
                {userInitials}
              </div>
              {/* Export counter */}
              <span className="fs-badge fs-badge--beta" style={{ fontSize: 10 }}>
                {exportsUsed} / {exportsMax}
              </span>
              {/* Chevron */}
              <svg
                width="10" height="10" viewBox="0 0 10 10" fill="none"
                style={{
                  color: 'var(--fs-text-disabled)',
                  transform: menuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {menuOpen && (
              <div className="fs-account-menu__dropdown">
                <div style={{
                  padding: '8px 16px 10px',
                  borderBottom: '1px solid var(--fs-border)',
                  marginBottom: 4,
                }}>
                  <p style={{ fontFamily: 'var(--fs-font-headline)', fontSize: 13, fontWeight: 700, color: 'var(--fs-text)', marginBottom: 2 }}>
                    {userName}
                  </p>
                  <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 11, color: 'var(--fs-text-disabled)' }}>
                    {exportsUsed} of {exportsMax} exports used this week
                  </p>
                </div>
                <a href="#settings" className="fs-account-menu__item" onClick={() => setMenuOpen(false)}>
                  Settings
                </a>
                <button className="fs-account-menu__item fs-account-menu__item--danger" onClick={handleSignOut}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
